import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { usePortalLanguage } from "@/contexts/PortalLanguageContext";

const NAVY = "#1A1A2E";

function P({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <p className={`text-gray-300 leading-relaxed ${className}`}>{children}</p>;
}

function Section({ num, title, children }: { num: number; title: string; children: React.ReactNode }) {
  return (
    <section className="mt-12 first:mt-0">
      <h2 className="text-xl font-bold text-white mb-4">
        {num}. {title}
      </h2>
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  );
}

function Sub({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-white font-semibold mb-1">{title}</p>
      <P>{children}</P>
    </div>
  );
}

function AddressBlock() {
  return (
    <div className="mt-2 text-sm text-gray-400 leading-relaxed">
      <p className="font-medium text-gray-300">16349684 Canada Inc. (dba Fyrris)</p>
      <p>23 Rue des Lilas, Bromont, QC J2L 1M4, Canada</p>
      <p className="mt-3 font-medium text-gray-300">Fyrris Inc.</p>
      <p>911 N Buffalo Dr, Suite 202</p>
      <p>Las Vegas, NV 89128, USA</p>
    </div>
  );
}

export default function Terms() {
  const { t } = usePortalLanguage();
  const { pathname } = useLocation();
  const locale = pathname.startsWith("/fr") ? "fr" : pathname.startsWith("/es") ? "es" : "en";

  const isEnglish = locale === "en";

  return (
    <>
      <Helmet>
        <title>{t("terms.meta.title", "Terms & Conditions — Fyrris Platform")}</title>
        <meta name="description" content="Terms and Conditions for the Fyrris app and platform — consumer app use and retailer subscriptions." />
      </Helmet>

      <div style={{ backgroundColor: NAVY }} className="min-h-screen">
        <div className="max-w-3xl mx-auto px-6 pt-12 pb-24 md:pt-14 md:pb-32">

          {/* Page header */}
          <div className="mb-10 pb-8 border-b border-gray-700">
            <p className="text-xs font-semibold uppercase tracking-widest text-amber-400 mb-3">
              {t("terms.header.label", "Legal")}
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t("terms.header.title", "Terms & Conditions — Fyrris")}
            </h1>
            <p className="text-sm text-gray-500">
              {t("terms.header.effective", "Effective Date:")} May 1, 2026
              {"  ·  "}
              {t("terms.header.updated", "Last Updated:")} October 24, 2026
            </p>
          </div>

          {/* Non-English notice */}
          {!isEnglish && (
            <div className="mb-10 rounded-xl bg-gray-900 border border-gray-700 p-5">
              <p className="text-gray-300 text-sm leading-relaxed">
                {locale === "fr"
                  ? "Ce document est disponible en anglais uniquement. Une version traduite sera fournie sur demande à support@fyrris.com"
                  : "Este documento está disponible solo en inglés. Se proporcionará una versión traducida a pedido a support@fyrris.com"}
              </p>
            </div>
          )}

          {/* Preamble */}
          <div className="mb-12 p-6 rounded-xl bg-gray-900 border border-gray-700">
            <P className="text-sm font-medium text-gray-200 leading-relaxed uppercase tracking-wide">
              PLEASE READ THESE TERMS AND CONDITIONS CAREFULLY BEFORE ACCESSING OR USING THE
              FYRRIS PLATFORM. BY CLICKING "I AGREE," COMPLETING ACCOUNT REGISTRATION, OR
              OTHERWISE ACCESSING OR USING THE PLATFORM, YOU ("RETAILER") AGREE TO BE BOUND
              BY THESE TERMS ON BEHALF OF YOURSELF AND THE BUSINESS ENTITY YOU REPRESENT.
            </P>
          </div>

          {/* ─── Consumer App Notice ──────────────────────────────────────────── */}
          <div className="mb-12 rounded-xl bg-gray-900 border border-gray-700 overflow-hidden">
            <div className="px-6 py-3 bg-amber-500/10 border-b border-gray-700">
              <p className="text-amber-400 text-xs font-semibold uppercase tracking-widest">For Consumer App Users</p>
            </div>
            <div className="px-6 py-5 flex flex-col gap-3">
              <P>
                The <strong className="text-white">Fyrris mobile app</strong> (available free on the Apple App Store) lets you browse
                and visualize hearth, patio, and barbecue products in augmented reality — no account or payment required.
                By downloading or using the Fyrris app, you agree to these Terms and our{" "}
                <a href="/privacy" className="text-amber-400 underline">Privacy Policy</a>.
              </P>
              <div>
                <p className="text-white font-semibold mb-1">AR Accuracy Disclaimer</p>
                <P>
                  3D models and AR visualizations in the Fyrris app are <strong className="text-white">representative of physical products</strong>,
                  not exact replicas. Colours, textures, surface finishes, and dimensions may vary from the
                  manufactured item due to screen calibration, lighting conditions, and the inherent limitations
                  of 3D modelling. Always verify final specifications with the retailer before purchase.
                </P>
              </div>
              <div>
                <p className="text-white font-semibold mb-1">Camera &amp; Device Permissions</p>
                <P>
                  Camera access is used solely to power AR surface detection on your device via ARKit. No
                  camera images or video are transmitted to Fyrris or any third party. See our{" "}
                  <a href="/privacy" className="text-amber-400 underline">Privacy Policy §2.3</a> for full details.
                </P>
              </div>
              <div>
                <p className="text-white font-semibold mb-1">Intellectual Property</p>
                <P>
                  The Fyrris app, its AR processing pipeline, and all platform technology are owned exclusively
                  by Fyrris. Product designs, brand names, and trademarks displayed in the app remain the
                  property of their respective manufacturers and retailers. Fyrris claims no ownership over
                  manufacturer product designs.
                </P>
              </div>
              <div>
                <p className="text-white font-semibold mb-1">Governing Law (Consumer Users)</p>
                <P>
                  Your use of the Fyrris consumer app is governed by the laws of the Province of Quebec, Canada,
                  without regard to conflict of law principles, and subject to any mandatory consumer-protection
                  laws of your province or state of residence.
                </P>
              </div>
              <P className="text-sm text-gray-500 mt-1">
                The sections below (Sections 1–20) govern the Fyrris <strong className="text-gray-400">Retailer Platform</strong> subscription.
                Consumer app users are not subject to Sections 3–10 or 15.2's liability cap, which apply only to
                business-to-business agreements.
              </P>
            </div>
          </div>

          {/* ─── Section 1 ────────────────────────────────────────────────────── */}
          <Section num={1} title="Parties and Agreement">
            <P>
              These Retailer Subscription Terms &amp; Conditions ("Agreement") are entered into between you and
              the applicable Fyrris entity based on where your retail business is located:
            </P>
            <div className="rounded-xl bg-gray-900 border border-gray-700 p-5 text-sm text-gray-300 space-y-4">
              <div>
                <p className="font-semibold text-white mb-1">For retailers based in Canada:</p>
                <p>16349684 Canada Inc. (doing business as Fyrris)</p>
                <p>23 Rue des Lilas, Bromont, Quebec, Canada J2L 1M4</p>
              </div>
              <div>
                <p className="font-semibold text-white mb-1">For retailers based in the United States:</p>
                <p>Fyrris, Inc.</p>
                <p>911 N Buffalo Dr, Suite 202</p>
                <p>Las Vegas, NV 89128, USA</p>
              </div>
            </div>
            <P>
              The applicable entity is referred to herein as "Fyrris," "we," "us," or "our."
              16349684 Canada Inc., a corporation organized under the laws of Canada, and Fyrris Inc.,
              a corporation organized under the laws of Nevada.
            </P>
            <P>
              By accessing or using the Fyrris Platform, you represent that you have the legal
              authority to enter into this Agreement on behalf of yourself and, where applicable,
              the business entity you represent. If you do not have such authority, or if you do
              not agree to these Terms, you must not access or use the Platform.
            </P>
            <P>
              These Terms, together with Fyrris's Privacy Policy and any Order Forms or
              Subscription confirmations, constitute the entire Agreement between the parties
              with respect to the Platform.
            </P>
          </Section>

          {/* ─── Section 2 ────────────────────────────────────────────────────── */}
          <Section num={2} title="Definitions">
            <P>"<strong className="text-white">Platform</strong>" means the Fyrris software-as-a-service application, APIs, portals, and associated services, accessible at https://fyrris.com and through Fyrris mobile applications.</P>
            <P>"<strong className="text-white">Subscription</strong>" means the recurring paid plan selected by Retailer to access the Platform.</P>
            <P>"<strong className="text-white">Order Form</strong>" means the quote, subscription confirmation or invoice issued by Fyrris to Retailer that sets out Retailer's plan, fees, billing frequency, term and any special conditions.</P>
            <P>"<strong className="text-white">Product Catalogue</strong>" means the collection of product listings, images, descriptions, and associated 3D/AR assets uploaded or generated for Retailer's account.</P>
            <P>"<strong className="text-white">AR Content</strong>" means three-dimensional models, augmented reality visualizations, and related media created by or for Fyrris using Retailer's product data.</P>
            <P>"<strong className="text-white">Consumer App</strong>" means the Fyrris mobile application made available to end consumers free of charge to browse and visualize products.</P>
            <P>"<strong className="text-white">User Data</strong>" means any data submitted to or generated through the Platform by Retailer or its employees, including product information, location data, and analytics.</P>
            <P>"<strong className="text-white">Confidential Information</strong>" means non-public information disclosed by one party to the other that is designated as confidential or that reasonably should be understood to be confidential given the nature of the information.</P>
          </Section>

          {/* ─── Section 3 ────────────────────────────────────────────────────── */}
          <Section num={3} title="Eligibility">
            <P>To use the Platform as a Retailer, you must: (a) be a legally registered business entity or sole proprietor operating in the hearth, patio, and barbecue (HPB) specialty retail industry or adjacent markets; (b) be at least 18 years of age; (c) have the legal authority to enter into binding contracts; and (d) not be located in, or a national of, any jurisdiction subject to applicable trade sanctions or export control laws.</P>
            <P>Fyrris reserves the right to verify eligibility at any time and to suspend or terminate accounts that do not meet these requirements.</P>
          </Section>

          {/* ─── Section 4 ────────────────────────────────────────────────────── */}
          <Section num={4} title="Account Registration and Security">
            <Sub title="4.1 Registration">
              You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete. Inaccurate information may result in immediate suspension or termination of your account.
            </Sub>
            <Sub title="4.2 Credentials">
              You are responsible for maintaining the confidentiality of your account credentials. You agree to notify Fyrris immediately at support@fyrris.com of any unauthorized access to or use of your account. Fyrris is not liable for any loss or damage arising from your failure to maintain the security of your credentials.
            </Sub>
            <Sub title="4.3 Account Responsibility">
              You are responsible for all activity that occurs under your account, whether or not authorized by you. You may not share your account with third parties or allow multiple users to access the Platform under a single account unless expressly permitted by your Subscription plan.
            </Sub>
          </Section>

          {/* ─── Section 5 ────────────────────────────────────────────────────── */}
          <Section num={5} title="Platform License">
            <Sub title="5.1 Grant">
              Subject to your compliance with these Terms and timely payment of applicable Subscription fees, Fyrris grants you a limited, non-exclusive, non-transferable, non-sublicensable license to access and use the Platform solely for your internal business purposes as a participating retailer during the Subscription term.
            </Sub>
            <Sub title="5.2 Restrictions">
              You may not: (a) copy, modify, or create derivative works of the Platform; (b) reverse engineer, disassemble, or decompile any component of the Platform; (c) sell, resell, rent, lease, or sublicense access to the Platform; (d) use the Platform to build a competing product or service; (e) use automated means to access the Platform in a manner that exceeds reasonable usage; or (f) remove or alter any proprietary notices or labels.
            </Sub>
            <Sub title="5.3 Reservation of Rights">
              All rights not expressly granted to you are reserved by Fyrris. The Platform is licensed, not sold.
            </Sub>
          </Section>

          {/* ─── Section 6 ────────────────────────────────────────────────────── */}
          <Section num={6} title="Subscription Plans and Fees">
            <Sub title="6.1 Plans">
              Your Subscription plan, fees, billing frequency, included features and location limits are set out in your Order Form. Plan information on the Fyrris website is provided for general reference; your Order Form governs your Subscription.
            </Sub>
            <Sub title="6.2 Billing">
              Annual Subscriptions are billed in full upon signup. Monthly Subscriptions (where available) are billed on the same calendar day each month. All fees are in USD unless otherwise specified. Fyrris uses a third-party payment processor; by providing payment information you authorize recurring charges.
            </Sub>
            <Sub title="6.3 Price Changes">
              Fyrris may change Subscription fees for future billing cycles upon at least 30 days' written notice. Any rate commitment stated in your Order Form, including any rate lock, will be honoured for the period and on the conditions stated there. Retailers who subscribed under the former Founding Member Plan keep their locked annual rate for the life of an active Subscription; a lapse in payment that results in account cancellation forfeits the locked rate.
            </Sub>
            <Sub title="6.4 Taxes">
              Fees are exclusive of applicable taxes. You are responsible for all sales, use, value-added, and other taxes imposed by any governmental authority, excluding taxes on Fyrris's net income.
            </Sub>
            <Sub title="6.5 Late Payments">
              Overdue amounts accrue interest at the rate of 1.5% per month (or the maximum permitted by law, whichever is lower) from the due date. Fyrris may suspend Platform access for accounts with overdue balances after providing 7 days' notice.
            </Sub>
          </Section>

          {/* ─── Section 7 ────────────────────────────────────────────────────── */}
          <Section num={7} title="Free Trial and Promotional Offers">
            <P>Fyrris may offer free trial periods or promotional pricing at its discretion. At the end of any trial period, your Subscription will automatically convert to a paid plan unless cancelled before the trial expires. Promotional rates apply only to the period specified and do not renew at the promotional price unless Fyrris expressly states otherwise in writing.</P>
          </Section>

          {/* ─── Section 8 ────────────────────────────────────────────────────── */}
          <Section num={8} title="Cancellation and Termination">
            <Sub title="8.1 Cancellation by Retailer">
              You may cancel your Subscription at any time through your account settings or by contacting support@fyrris.com. Cancellation takes effect at the end of the current billing period. Access to the Platform continues until the end of the paid period.
            </Sub>
            <Sub title="8.2 Termination by Fyrris">
              Fyrris may suspend or terminate your account immediately, with or without notice, if: (a) you materially breach these Terms and fail to cure such breach within 10 days of written notice; (b) you engage in fraudulent, abusive, or unlawful conduct; (c) continued operation of your account poses a security or legal risk to Fyrris or other users; or (d) Fyrris ceases to offer the Platform.
            </Sub>
            <Sub title="8.3 Effect of Termination">
              Upon termination: (a) your license to use the Platform immediately terminates; (b) you must cease all use of the Platform and delete any locally cached Platform data; (c) Fyrris may delete your account and User Data after a 30-day grace period, unless legally required to retain it; (d) provisions of these Terms that by their nature should survive termination will survive.
            </Sub>
          </Section>

          {/* ─── Section 9 ────────────────────────────────────────────────────── */}
          <Section num={9} title="Refund Policy">
            <Sub title="9.1 General Policy">
              All Subscription fees are non-refundable except as expressly stated in this Section or as required by applicable law.
            </Sub>
            <Sub title="9.2 Satisfaction Guarantees">
              Any money-back or satisfaction guarantee applies only if it is stated in your Order Form, and on the terms stated there. Retailers who subscribed under the former Founding Member Plan remain eligible for its 90-day guarantee: a full refund of the annual Subscription fee if cancellation is requested within 90 days of the initial payment date. To request a refund under a guarantee, contact support@fyrris.com with your account email and a brief description of your reason for cancellation. Refunds are processed within 10 business days to the original payment method. A guarantee applies once per Retailer entity and is not transferable.
            </Sub>
            <Sub title="9.3 Other Refunds">
              Except as stated in Section 9.2, Subscriptions are not eligible for pro-rated refunds upon mid-period cancellation unless termination was caused by a material breach by Fyrris.
            </Sub>
          </Section>

          {/* ─── Section 10 ────────────────────────────────────────────────────── */}
          <Section num={10} title="Retailer Responsibilities and Acceptable Use">
            <Sub title="10.1 Content Accuracy">
              You are solely responsible for the accuracy, completeness, and legality of all product data, images, pricing, and other information you submit to the Platform. Fyrris is not responsible for inaccuracies in your Product Catalogue.
            </Sub>
            <Sub title="10.2 Prohibited Conduct">
              You agree not to use the Platform to: (a) upload content that infringes any third-party intellectual property rights; (b) submit false, misleading, or deceptive product information; (c) harass, abuse, or harm consumers or other users; (d) introduce malware, viruses, or other harmful code; (e) violate any applicable law, regulation, or third-party right; or (f) circumvent any access controls, security features, or usage limits.
            </Sub>
            <Sub title="10.3 Compliance">
              You are responsible for complying with all laws applicable to your business operations, including consumer protection, privacy, advertising, and product safety laws in the jurisdictions in which you operate.
            </Sub>
          </Section>

          {/* ─── Section 11 ────────────────────────────────────────────────────── */}
          <Section num={11} title="Product Catalogue and AR Visualization">
            <Sub title="11.1 Licence to Retailer Content">
              You grant Fyrris a non-exclusive, worldwide, royalty-free licence to use, reproduce, process, adapt, and display your Product Catalogue and associated materials solely for the purpose of providing the Platform and Consumer App features to you and to consumers.
            </Sub>
            <Sub title="11.2 AR Processing">
              Fyrris will use commercially reasonable efforts to generate AR Content from your Product Catalogue within the timeframes indicated in your onboarding documentation. Fyrris does not warrant that AR Content will be a pixel-perfect representation of physical products; minor variations in colour, texture, and scale are expected.
            </Sub>
            <Sub title="11.3 Retailer Approval">
              Fyrris may make AR Content live in the Consumer App after reasonable review. You may request corrections to AR Content by submitting a support request. Fyrris will use reasonable efforts to address corrections within 10 business days.
            </Sub>
            <Sub title="11.4 Content Removal">
              Fyrris reserves the right to remove any product listing or AR Content that, in Fyrris's reasonable determination, violates these Terms, applicable law, or third-party rights, after providing reasonable notice to you where practicable.
            </Sub>
          </Section>

          {/* ─── Section 12 ────────────────────────────────────────────────────── */}
          <Section num={12} title="Data Privacy and Security">
            <Sub title="12.1 Privacy Policy">
              Our collection and use of personal data is governed by the Fyrris Privacy Policy, available at{" "}
              <a href="https://fyrris.com/privacy" className="underline hover:text-white transition">https://fyrris.com/privacy</a>,
              which is incorporated herein by reference.
            </Sub>
            <Sub title="12.2 Retailer Data">
              Fyrris processes User Data on your behalf in accordance with your instructions as documented in these Terms and our Privacy Policy. You retain ownership of your User Data. Fyrris will not sell your User Data to third parties.
            </Sub>
            <Sub title="12.3 Security">
              Fyrris implements commercially reasonable technical and organisational measures to protect User Data against unauthorised access, disclosure, alteration, and destruction. However, no security system is impenetrable, and Fyrris cannot guarantee absolute security.
            </Sub>
            <Sub title="12.4 Consumer Data">
              Consumer data collected through the Consumer App is governed by the Fyrris consumer-facing Privacy Policy. Retailers do not receive identifiable consumer personal data through the Platform without the consumer's express consent.
            </Sub>
          </Section>

          {/* ─── Section 13 ────────────────────────────────────────────────────── */}
          <Section num={13} title="Intellectual Property">
            <Sub title="13.1 Fyrris IP">
              The Platform, including all software, design, technology, algorithms, AR processing pipelines, trademarks, and documentation, is and remains the sole and exclusive property of Fyrris and its licensors. Nothing in these Terms transfers any ownership interest in Fyrris IP to you.
            </Sub>
            <Sub title="13.2 Retailer IP">
              You retain all ownership rights in your Product Catalogue, brand assets, and other original materials you provide to Fyrris. Except for the licence granted in Section 11.1, Fyrris acquires no rights in your intellectual property.
            </Sub>
            <Sub title="13.3 Feedback">
              If you provide suggestions, ideas, or feedback about the Platform ("Feedback"), you grant Fyrris a perpetual, irrevocable, royalty-free licence to use such Feedback for any purpose without compensation or attribution to you.
            </Sub>
          </Section>

          {/* ─── Section 14 ────────────────────────────────────────────────────── */}
          <Section num={14} title="Confidentiality">
            <P>Each party agrees to hold the other's Confidential Information in strict confidence, to use it only for the purposes of this Agreement, and to disclose it only to employees or contractors with a need to know who are bound by obligations of confidentiality at least as protective as those in this Section. These obligations do not apply to information that: (a) is or becomes publicly known through no breach of this Agreement; (b) was rightfully known to the receiving party before disclosure; (c) is independently developed without use of Confidential Information; or (d) is required to be disclosed by law or court order, provided the receiving party gives reasonable prior written notice to the disclosing party and cooperates in seeking a protective order.</P>
          </Section>

          {/* ─── Section 15 ────────────────────────────────────────────────────── */}
          <Section num={15} title="Disclaimers and Limitation of Liability">
            <Sub title="15.1 Disclaimer of Warranties">
              THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR UNINTERRUPTED OR ERROR-FREE OPERATION. FYRRIS DOES NOT WARRANT THAT THE PLATFORM WILL MEET YOUR REQUIREMENTS OR THAT AR CONTENT WILL PERFECTLY REPRESENT PHYSICAL PRODUCTS.
            </Sub>
            <Sub title="15.2 Limitation of Liability">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, FYRRIS'S TOTAL CUMULATIVE LIABILITY TO YOU FOR ANY CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR THE PLATFORM, WHETHER IN CONTRACT, TORT, OR OTHERWISE, SHALL NOT EXCEED THE GREATER OF: (A) THE TOTAL FEES PAID BY YOU TO FYRRIS IN THE 12 MONTHS IMMEDIATELY PRECEDING THE CLAIM; OR (B) $500 USD.
            </Sub>
            <Sub title="15.3 Exclusion of Consequential Damages">
              IN NO EVENT SHALL FYRRIS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, REVENUE, DATA, GOODWILL, OR BUSINESS INTERRUPTION, EVEN IF FYRRIS HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </Sub>
            <Sub title="15.4 Essential Basis">
              THE LIMITATIONS IN THIS SECTION REFLECT A REASONABLE ALLOCATION OF RISK AND ARE AN ESSENTIAL BASIS OF THE BARGAIN BETWEEN THE PARTIES. FYRRIS WOULD NOT ENTER INTO THIS AGREEMENT WITHOUT THESE LIMITATIONS.
            </Sub>
          </Section>

          {/* ─── Section 16 ────────────────────────────────────────────────────── */}
          <Section num={16} title="Indemnification">
            <P>You agree to defend, indemnify, and hold harmless Fyrris and its officers, directors, employees, agents, and licensors from and against any claims, liabilities, damages, judgments, awards, losses, costs, and expenses (including reasonable attorneys' fees) arising out of or relating to: (a) your use of the Platform in violation of these Terms; (b) your Product Catalogue or any content you submit to the Platform; (c) your violation of any applicable law or third-party right; or (d) your business operations.</P>
          </Section>

          {/* ─── Section 17 ────────────────────────────────────────────────────── */}
          <Section num={17} title="Dispute Resolution and Governing Law">
            <Sub title="17.1 Governing Law">
              These Terms are governed by and construed in accordance with applicable law, without regard to conflict of law principles. For Canadian retailers: governed by the laws of the Province of Quebec, Canada. For US retailers: governed by the laws of the State of Nevada, USA.
            </Sub>
            <Sub title="17.2 Informal Resolution">
              Before initiating formal proceedings, the parties agree to attempt to resolve any dispute informally by contacting Fyrris at support@fyrris.com. Fyrris will attempt to resolve the dispute within 30 days of receiving notice.
            </Sub>
            <Sub title="17.3 Binding Arbitration">
              If informal resolution fails, disputes shall be resolved by binding arbitration administered by a mutually agreed arbitration body under its commercial arbitration rules, with proceedings conducted in the applicable jurisdiction (for Canadian retailers: Montreal, Quebec, Canada; for US retailers: Las Vegas, Nevada, USA). The arbitrator's decision shall be final and binding.
            </Sub>
            <Sub title="17.4 Exceptions">
              Notwithstanding the arbitration clause, either party may seek injunctive or other equitable relief in any court of competent jurisdiction to protect intellectual property rights or Confidential Information.
            </Sub>
            <Sub title="17.5 Class Action Waiver">
              All claims must be brought in an individual capacity and not as a class or representative action. You waive any right to participate in a class action lawsuit or class-wide arbitration.
            </Sub>
          </Section>

          {/* ─── Section 18 ────────────────────────────────────────────────────── */}
          <Section num={18} title="General Provisions">
            <Sub title="18.1 Entire Agreement">
              These Terms, together with the Privacy Policy and any applicable Order Forms, constitute the entire agreement between you and Fyrris regarding the Platform and supersede all prior agreements and understandings.
            </Sub>
            <Sub title="18.2 Amendments">
              Fyrris may update these Terms at any time. We will provide at least 30 days' notice of material changes by email or Platform notification. Continued use of the Platform after the effective date of changes constitutes acceptance.
            </Sub>
            <Sub title="18.3 Severability">
              If any provision of these Terms is found unenforceable, that provision will be modified to the minimum extent necessary to make it enforceable, and the remaining provisions will continue in full force and effect.
            </Sub>
            <Sub title="18.4 Waiver">
              Failure by either party to enforce any right under these Terms does not constitute a waiver of that right.
            </Sub>
            <Sub title="18.5 Assignment">
              You may not assign these Terms or your account without Fyrris's prior written consent. Fyrris may assign these Terms in connection with a merger, acquisition, or sale of substantially all assets.
            </Sub>
            <Sub title="18.6 Force Majeure">
              Neither party is liable for delays or failures in performance resulting from causes beyond its reasonable control, including natural disasters, pandemics, government actions, or internet infrastructure failures.
            </Sub>
            <div>
              <p className="text-white font-semibold mb-1">18.7 Notices</p>
              <P>Legal notices to Fyrris must be sent in writing to the applicable entity address below. Notices to you will be sent to the email address on your account.</P>
              <AddressBlock />
            </div>
          </Section>

          {/* ─── Section 19 ────────────────────────────────────────────────────── */}
          <Section num={19} title="Contact Information">
            <P>For support, billing enquiries, refund requests, or questions about these Terms, please contact:</P>
            <div className="mt-2 rounded-xl bg-gray-900 border border-gray-700 p-5 text-sm text-gray-300 space-y-1">
              <p className="font-semibold text-white">Fyrris</p>
              <p>Email: <a href="mailto:support@fyrris.com" className="underline hover:text-white transition">support@fyrris.com</a></p>
              <p>General inquiries: <a href="mailto:support@fyrris.com" className="underline hover:text-white transition">support@fyrris.com</a></p>
              <div className="mt-3 pt-3 border-t border-gray-700">
                <p className="text-gray-500 mb-2">Mailing addresses:</p>
                <p className="font-medium text-gray-300">16349684 Canada Inc. (dba Fyrris)</p>
                <p>23 Rue des Lilas, Bromont, QC J2L 1M4, Canada</p>
                <p className="mt-2 font-medium text-gray-300">Fyrris Inc.</p>
                <p>911 N Buffalo Dr, Suite 202</p>
                <p>Las Vegas, NV 89128, USA</p>
              </div>
            </div>
          </Section>

          {/* ─── Section 20 ────────────────────────────────────────────────────── */}
          <Section num={20} title="Entire Agreement">
            <P>These Terms and Conditions, together with the Fyrris Privacy Policy (available at{" "}
              <a href="https://fyrris.com/privacy" className="underline hover:text-white transition">https://fyrris.com/privacy</a>)
              {" "}and any applicable Subscription Order Form, constitute the entire agreement between Retailer and Fyrris with respect to the Platform. They supersede all prior or contemporaneous agreements, representations, warranties, and understandings, whether written or oral, relating to the subject matter hereof. In the event of any conflict between these Terms and an Order Form, the Order Form shall prevail with respect to its specific subject matter.</P>
          </Section>

          {/* Footer note */}
          <div className="mt-16 pt-8 border-t border-gray-700 text-xs text-gray-600 text-center space-y-1">
            <p>16349684 Canada Inc. (dba Fyrris) · 23 Rue des Lilas, Bromont, QC J2L 1M4, Canada</p>
            <p>Fyrris Inc. · 911 N Buffalo Dr, Suite 202, Las Vegas, NV 89128, USA</p>
            <p>support@fyrris.com</p>
            <p className="mt-1">© 2026 Fyrris Inc. All rights reserved.</p>
          </div>

        </div>
      </div>
    </>
  );
}
